/**
 * V4 Scenario → V3 Scenario shape. Tags `source: 'v4'` and preserves the raw
 * V4 payload on `v4Data` so `toV4ScenarioRequest` can fall back to it for
 * fields the V3 editor can't surface (multi-trigger, V4-native conditions).
 */

import { toV3ActionType, toV3TriggerSource } from './translations';
import { fansOutInV4, toV3ActionConfig } from './actionConfigs';
import { triggerConfigs, triggerConfigKeys } from '../../utils/models/scenarios/triggerConfigs';
import { forAnyObject, forConcerningObject } from '../../utils/models/Scenario';
import { decodeV4Schedule, isValidScheduleMasks } from './schedule';
import ScenarioLog from '../../utils/models/ScenarioLog';

// V4 replaced the flat trigger id arrays with nested entity filters:
//   devices: { include?, any?, concerning?, inverted? }, teams/geofences the
// same without `concerning`. Read nested first, falling back to the flat keys
// for an environment still behind the migration — but an empty nested filter
// never out-votes a populated legacy array.
const filterIds = (filter, legacyIds) => {
    const include = Array.isArray(filter?.include) ? filter.include : [];
    if (include.length) return include;
    const legacy = Array.isArray(legacyIds) ? legacyIds : [];
    return legacy.length ? legacy : include;
};
// Whether the filter narrows anything. `concerning` isn't tested: it's declared
// on the devices filter only, and this is called for teams/geofences.
const filterSelects = (filter, ids) => !!(ids.length || filter?.any);

// V3 triggerConfig sub-fields depend on the trigger category — route V4 ids
// into the matching V3 key per category.
function v4TriggerToV3Config(v4Trigger, v3Trigger, idMaps = {}) {
    if (!v4Trigger) return [];
    // V4 ids are UUIDs; map back to v3 numeric ids for the picker (unmapped pass through).
    const deviceUuidToV3Id = idMaps.deviceUuidToV3Id || {};
    const devicesFilter = v4Trigger.devices;
    const teamsFilter = v4Trigger.teams;
    const geofencesFilter = v4Trigger.geofences;

    const deviceIds = filterIds(devicesFilter, v4Trigger.device_ids)
        .map(uuid => deviceUuidToV3Id[uuid] ?? uuid);
    const teamIds = filterIds(teamsFilter, v4Trigger.team_ids);
    const geofenceIds = filterIds(geofencesFilter, v4Trigger.geofence_ids);

    const category = triggerConfigs[v3Trigger];
    if (category === triggerConfigKeys.geofences) {
        // A geofence trigger can't be saved empty, so no ids always means "any".
        return geofenceIds.length ? geofenceIds : [forAnyObject];
    }
    if (category === triggerConfigKeys.beacons) {
        // Dead branch: no SDK version has ever carried a beacons filter.
        return Array.isArray(v4Trigger.beacon_ids) ? v4Trigger.beacon_ids : [];
    }
    if (category === triggerConfigKeys.teamAttendance) {
        // `teamId` stays because `Scenario.containsTeamIds` reads the singular key.
        return {
            teamId: teamIds[0] || null,
            teamIds,
            inverted: !!(teamsFilter?.inverted ?? v4Trigger.inverted),
        };
    }

    // Pick the source from which filter is populated, not from which legacy
    // array is non-empty: under the nested shape they are all empty, which
    // turned every team and geofence trigger into a device trigger.
    const source = filterSelects(teamsFilter, teamIds) ? 'teams'
        : filterSelects(geofencesFilter, geofenceIds) ? 'geofences'
        : 'devices';
    const activeFilter = source === 'teams' ? teamsFilter
        : source === 'geofences' ? geofencesFilter
        : devicesFilter;
    // `inverted` moved from the trigger into each filter.
    const inverted = !!(activeFilter?.inverted ?? v4Trigger.inverted);

    const base = { source, inverted };
    if (source === 'devices') {
        // Inverted can't mean "any".
        const isAny = !!devicesFilter?.any && !inverted;
        base.deviceIds = [
            ...(devicesFilter?.concerning ? [forConcerningObject] : []),
            ...(isAny ? [forAnyObject] : []),
            ...deviceIds,
        ];
        if (!base.deviceIds.length) {
            // No device filter at all is an unfiltered, account-wide trigger —
            // what V4 auto-creates for an ARC link. Empty fails
            // `triggerConfigured`, disabling Save AND the actions accordion.
            base.deviceIds =
                category === triggerConfigKeys.devices && !inverted ? [forAnyObject] : [];
        }
    }
    // Reached only when `filterSelects` was true, so empty here means `any`.
    // Same sentinel rule as devices above.
    else if (source === 'teams') base.teamIds = teamIds.length ? teamIds : [forAnyObject];
    else if (source === 'geofences') {
        base.geofenceIds = geofenceIds.length ? geofenceIds : [forAnyObject];
    }
    return base;
}

/**
 * Fold back what `toV4ActionConfigs` fans out: consecutive `change_team` actions
 * sharing a join/leave verb become one V3 action again, or every save
 * multiplies the rows the user sees. Only adjacent ones merge — that is how the
 * projection emits them.
 */
function coalesceFannedOutActions(actions) {
    const out = [];
    for (const action of actions) {
        const previous = out[out.length - 1];
        const mergeable =
            previous &&
            fansOutInV4(action.v4Type) &&
            previous.v4Type === action.v4Type &&
            previous.config.action === action.config.action;
        if (mergeable) {
            previous.config.teamIds = [...previous.config.teamIds, ...action.config.teamIds];
            continue;
        }
        out.push(action);
    }
    return out.map(({ v4Type, ...rest }) => rest);
}

// V4 supports multiple triggers; V3 only one. Use the first; the rest ride
// along on v4Data and would be dropped on a V3-driven save.
export function mapV4ScenarioToV3(v4Scenario, { idMaps = {}, timezone } = {}) {
    const v4Triggers = Array.isArray(v4Scenario.triggers) ? v4Scenario.triggers : [];
    const v4Actions = Array.isArray(v4Scenario.actions) ? v4Scenario.actions : [];
    const primaryV4Trigger = v4Triggers[0];

    const v3Trigger = primaryV4Trigger ? toV3TriggerSource(primaryV4Trigger.source) : '';
    const triggerConfig = v4TriggerToV3Config(primaryV4Trigger, v3Trigger, idMaps);

    const v3Actions = coalesceFannedOutActions(
        v4Actions.map(a => {
            const v4Type = a?.type || '';
            return {
                id: 0,
                type: toV3ActionType(v4Type),
                name: '',
                v4Type,
                config: toV3ActionConfig(v4Type, a?.config, { idMaps }),
            };
        })
    );
    const lastRun = ScenarioLog.fromLastTriggeredAt(
        v4Scenario.last_triggered_at,
        v4Scenario.id
    );

    return {
        id: v4Scenario.id,
        name: v4Scenario.name || '',
        trigger: v3Trigger,
        triggerConfig,
        accounts: null,
        actions: v3Actions,
        // Reuse the V3 card's existing Last run rendering. V4 exposes only the
        // latest timestamp rather than a full log collection.
        logs: lastRun ? [lastRun] : [],
        enabled: v4Scenario.is_active !== false,
        locked: false,
        visible: true,
        // Decode V4's UTC bitmasks into V3's account-local boolean[7][48] so the
        // editor reflects the saved schedule. Absent/malformed ⇒ [] ("always").
        schedule: isValidScheduleMasks(v4Scenario.schedule)
            ? decodeV4Schedule(v4Scenario.schedule, timezone)
            : [],
        source: 'v4',
        v4Data: v4Scenario,
    };
}
